/**
 * Endpoints where a 404 is a normal outcome rather than a defect.
 *
 * These are lookups of a thing the user arrived at by link — a story slug, a
 * suggestion-list token. The thing legitimately may not exist: stories get
 * merged or age out, and clients open links that were truncated by their mail
 * client somewhere between us and them. "Not found" is the honest answer, and
 * the page should say so.
 *
 * Reporting them as errors produced the single largest frontend Sentry issue —
 * 199 events across 18 users and 24 distinct endpoints, which is not one bug but
 * a long tail of dead links, each one also popping a generic "Request Failed"
 * modal at whoever clicked it.
 *
 * Deliberately narrow. 404 stays reportable everywhere else, because on any
 * other endpoint it means we asked for a route that should exist — exactly the
 * kind of breakage worth hearing about after a refactor.
 */
const NOT_FOUND_IS_A_STATE = [
    // GET /story/:slugOrId — an old link or bookmark to a story that has since
    // been merged away or aged out.
    /^\/story\/[^/?#]+\/?(\?|#|$)/,
    // GET /public/suggestion-lists/:publicToken — a client's emailed link.
    /^\/public\/suggestion-lists\/[^/?#]+\/?(\?|#|$)/,
    // GET /public/buckets/:bucketId/suggestions — the live public list.
    /^\/public\/buckets\/[^/?#]+\/suggestions\/?(\?|#|$)/,
    // GET /public/shared-artifacts/:publicToken — a "Send to client" link.
    // Same shape as the suggestion-list token above and the same failure mode:
    // mail clients truncate the URL, and the recipient may open it long after.
    /^\/public\/shared-artifacts\/[^/?#]+\/?(\?|#|$)/,
    // GET /public/signals/:publicToken — a Signal alert link.
    /^\/public\/signals\/[^/?#]+\/?(\?|#|$)/,
    // GET /saved-briefs/:id — a draft reached by id rather than from the list:
    // a ?expand= deep link, a "your draft is ready" notification, or a row
    // someone was sent. The draft may have been permanently deleted since, and
    // a hidden job intermediate is deliberately unaddressable by id. Both are
    // states, not breakage, and neither should raise a "Request Failed" modal
    // at someone who merely followed a link.
    /^\/saved-briefs\/[^/?#]+\/?(\?|#|$)/,
];

/**
 * Should this failure be treated as "the thing isn't there" rather than an error?
 *
 * @param {number|undefined} status HTTP status
 * @param {string|undefined} endpoint request path, as passed to handleApiError
 * @returns {boolean}
 */
export function isExpectedNotFound(status, endpoint) {
    if (Number(status) !== 404) return false;
    if (typeof endpoint !== 'string' || !endpoint) return false;
    // Tolerate a full URL or a missing leading slash.
    let path = endpoint;
    try {
        if (/^https?:\/\//i.test(path)) path = new URL(path).pathname;
    } catch (_) { /* fall through and match on the raw string */ }
    if (!path.startsWith('/')) path = `/${path}`;
    return NOT_FOUND_IS_A_STATE.some((re) => re.test(path));
}

export const __TEST_PATTERNS__ = NOT_FOUND_IS_A_STATE;
